import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

const API_BASE = "http://nissi-india.com/api/tyreLifeDetailsReport";

const toStr = (v) => String(v ?? "").trim();
const isSelect = (v) => !toStr(v) || toStr(v).toLowerCase() === "select";

const fmtDate = (v) => {
  if (!v) return "";
  return toStr(v);
};

const toNum = (v, d = 0) => {
  if (v === null || v === undefined || v === "") return d;
  const n = Number(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : d;
};

const fmtNum = (v) => {
  if (v === null || v === undefined || v === "") return "";
  const n = toNum(v, NaN);
  if (!Number.isFinite(n)) return toStr(v);
  return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
};

const pick = (...vals) => {
  for (const v of vals) {
    const s = String(v ?? "").trim();
    if (s) return s;
  }
  return "";
};

// ✅ handle any response shapes safely
const getRowsFromApi = (payload) => {
  const d = payload?.data ?? payload;
  const wrapped = d?.data ?? d?.result ?? d;
  return wrapped?.rows ?? wrapped?.recordset ?? wrapped?.list ?? wrapped?.items ?? [];
};

export default function TyreLifeDetailReportAllPrint() {
  const { state } = useLocation();
  const navigate = useNavigate();

  const didRun = useRef(false);
  const inFlight = useRef(false);

  // ✅ read current page + record count from navigate state
  const paramsFromState = useMemo(() => {
    const s = state || {};
    return {
      // session
      branchType: s.branchType ?? localStorage.getItem("branchtype") ?? "HO",
      loginUserId:
        s.loginUserId ??
        localStorage.getItem("userId") ??
        localStorage.getItem("loginuserid") ??
        "",
      sessionBranchId:
        s.sessionBranchId ?? Number(localStorage.getItem("branchid") || 0),

      // filters
      branchId: s.branchId ?? (localStorage.getItem("branchid") || "Select"),
      vehicleTypeIndex: s.vehicleTypeIndex ?? "Select",
      vehicleSubTypeId: s.vehicleSubTypeId ?? "Select",
      storeId: s.storeId ?? "Select",
      vehicleCode: s.vehicleCode ?? "",
      tyreSerialNo: s.tyreSerialNo ?? "",
      tyreSize: s.tyreSize ?? "",
      tyreMake: s.tyreMake ?? "",
      tyreModel: s.tyreModel ?? "",
      fromDate: s.fromDate ?? "",
      toDate: s.toDate ?? "",

      // ✅ pagination passed from listing page
      currentPage: Number(s.currentPage ?? 1),
      pageSize: Number(s.pageSize ?? 10),
      currentPageRecordCount: Number(s.currentPageRecordCount ?? 0),
      totalRecords: Number(s.totalRecords ?? 0),
    };
  }, [state]);

  const [groupedData, setGroupedData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // ✅ group by vehicle code using your API fields
  const groupByVehicle = (rows) => {
    const map = new Map();

    rows.forEach((row) => {
      const vehicleCode = pick(row?.fld_vehicle_code, row?.vehicleCode) || "Unknown";
      const vehicleNo = pick(row?.fld_vehicleregno, row?.fld_vehicle_no) || "";

      if (!map.has(vehicleCode)) {
        map.set(vehicleCode, { vehicleCode, vehicleNo, rows: [] });
      }
      map.get(vehicleCode).rows.push(row);
    });

    return Array.from(map.values());
  };

  /**
   * ✅ fetch ONLY current page records (one call)
   */
  const fetchPrint = async () => {
    if (inFlight.current) return;
    inFlight.current = true;

    try {
      setLoading(true);
      setErr("");
      setGroupedData([]);

      const { data } = await axios.get(`${API_BASE}/list`, {
        params: {
          branchType: paramsFromState.branchType,
          loginUserId: paramsFromState.loginUserId,
          branchId: paramsFromState.branchId,
          vehicleTypeIndex: paramsFromState.vehicleTypeIndex,
          vehicleSubTypeId: paramsFromState.vehicleSubTypeId,
          storeId: paramsFromState.storeId,
          vehicleCode: paramsFromState.vehicleCode,
          tyreSerialNo: paramsFromState.tyreSerialNo,
          tyreSize: paramsFromState.tyreSize,
          tyreMake: paramsFromState.tyreMake,
          tyreModel: paramsFromState.tyreModel,
          fromDate: paramsFromState.fromDate,
          toDate: paramsFromState.toDate,

          // ✅ use current page + pageSize passed from listing
          page: paramsFromState.currentPage,
          pageSize: paramsFromState.pageSize,

          sessionBranchId: paramsFromState.sessionBranchId,
        },
      });

      if (data?.success === false) {
        throw new Error(data?.message || "Failed to load print report");
      }

      const rows = getRowsFromApi(data);

      // ✅ group and set
      setGroupedData(groupByVehicle(rows));
    } catch (e) {
      setErr(e?.message || "Failed to load print report");
      setGroupedData([]);
    } finally {
      setLoading(false);
      inFlight.current = false;
    }
  };

  useEffect(() => {
    if (didRun.current) return;
    didRun.current = true;
    fetchPrint();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtersLabel = useMemo(() => {
    const p = paramsFromState;
    return [
      !isSelect(p.branchId) ? `Branch: ${p.branchId}` : null,
      p.vehicleCode ? `Vehicle Code: ${p.vehicleCode}` : null,
      p.tyreSerialNo ? `Tyre Serial: ${p.tyreSerialNo}` : null,
      p.tyreSize ? `Tyre Size: ${p.tyreSize}` : null,
      p.tyreMake ? `Tyre Make: ${p.tyreMake}` : null,
      p.tyreModel ? `Tyre Model: ${p.tyreModel}` : null,
      p.fromDate ? `From: ${p.fromDate}` : null,
      p.toDate ? `To: ${p.toDate}` : null,
    ]
      .filter(Boolean)
      .join(" | ");
  }, [paramsFromState]);

  // ✅ actual records printed (from grouped data)
  const printedRecords = useMemo(
    () => groupedData.reduce((sum, g) => sum + (g?.rows?.length || 0), 0),
    [groupedData]
  );

  return (
    <div className="w-full bg-white print:max-w-none print:mx-0">
      <style>{`
        @media print {
          body { margin: 0; padding: 10px; font-size: 10pt; }
          .no-print { display: none !important; }
          table { border-collapse: collapse; font-size: 7pt; }
          th, td { border: 1px solid #000 !important; padding: 2px !important; }
          .page-break { page-break-after: always; }
        }
        @page { size: A4 landscape; margin: 0.5in; }
      `}</style>

      <div className="p-2 print:p-1">
        {/* Title */}
        <div className="text-center mb-2 border-2 border-black py-1">
          <h2 className="text-base font-bold">TYRE COSTING REPORT</h2>

          <div className="text-xs mt-1">
            Branch Name:{" "}
            <b>{paramsFromState.branchId !== "Select" ? paramsFromState.branchId : "All"}</b>
          </div>

          <div className="text-xs">
            Date and Time: <b>{new Date().toLocaleString("en-GB")}</b>
          </div>

          {filtersLabel ? <div className="text-xs mt-1">{filtersLabel}</div> : null}

          {/* ✅ current page info */}
          <div className="text-xs mt-1">
            Page: <b>{paramsFromState.currentPage}</b> | Page Size: <b>{paramsFromState.pageSize}</b>
          </div>

          <div className="text-xs mt-1">
            Records in this page (passed): <b>{paramsFromState.currentPageRecordCount}</b> | Printed:{" "}
            <b>{printedRecords}</b>
          </div>

          {paramsFromState.totalRecords ? (
            <div className="text-xs mt-1">
              Total Records (all): <b>{paramsFromState.totalRecords}</b>
            </div>
          ) : null}
        </div>

        {/* Controls */}
        <div className="no-print mb-3 flex gap-2">
          <button onClick={() => navigate(-1)} className="px-4 py-2 border border-gray-400 rounded">
            Back
          </button>

          <button onClick={fetchPrint} className="px-4 py-2 border border-gray-400 rounded" disabled={loading}>
            {loading ? "Loading..." : "Reload"}
          </button>

          <button
            onClick={() => window.print()}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded shadow-lg"
            disabled={loading || printedRecords === 0}
          >
            🖨️ Print Report
          </button>
        </div>

        {err ? (
          <div className="no-print p-3 border border-red-400 text-red-700 rounded mb-3">{err}</div>
        ) : null}

        {/* Tables */}
        {groupedData.map((group, groupIdx) => {
          const totals = (group.rows || []).reduce(
            (acc, row) => {
              const fittingHr = toNum(row?.fld_fittingkmhr);
              const removingHr = toNum(row?.fld_RemovingKmhr);
              const runningHr = toNum(row?.fld_runninghrkm);
              const costOfTyre = toNum(row?.fld_tyrerate);
              const costHr = toNum(row?.fld_tyrecost);

              return {
                fittingHr: acc.fittingHr + fittingHr,
                removingHr: acc.removingHr + removingHr,
                runningHr: acc.runningHr + runningHr,
                costOfTyre: acc.costOfTyre + costOfTyre,
                costHr: acc.costHr + costHr,
              };
            },
            { fittingHr: 0, removingHr: 0, runningHr: 0, costOfTyre: 0, costHr: 0 }
          );

          return (
            <div key={groupIdx} className={groupIdx < groupedData.length - 1 ? "page-break mb-4" : "mb-4"}>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border-2 border-black text-xs">
                  <thead>
                    <tr className="bg-gray-200">
                      <th className="border border-black px-1 py-1 text-center">S.no</th>
                      <th className="border border-black px-1 py-1 text-center">Tyre serial No</th>
                      <th className="border border-black px-1 py-1 text-center">Tyre Size</th>
                      <th className="border border-black px-1 py-1 text-center">Tyre Make</th>
                      <th className="border border-black px-1 py-1 text-center">Tyre Model</th>
                      <th className="border border-black px-1 py-1 text-center">Vehicle NO</th>
                      <th className="border border-black px-1 py-1 text-center">Vehicle Code</th>
                      <th className="border border-black px-1 py-1 text-center">Original/RTD</th>
                      <th className="border border-black px-1 py-1 text-center">Fitting Date</th>
                      <th className="border border-black px-1 py-1 text-center">Removing Date</th>
                      <th className="border border-black px-1 py-1 text-center">Fitting Hr/KM</th>
                      <th className="border border-black px-1 py-1 text-center">Removing Hr/KM</th>
                      <th className="border border-black px-1 py-1 text-center">Running Hr/KM</th>
                      <th className="border border-black px-1 py-1 text-center">Cost of Tyre</th>
                      <th className="border border-black px-1 py-1 text-center">Cost Hr/KM</th>
                      <th className="border border-black px-1 py-1 text-center">Reason For Remove</th>
                    </tr>
                  </thead>

                  <tbody>
                    {(group.rows || []).map((r, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                        <td className="border border-black px-1 py-1 text-center">{idx + 1}</td>
                        <td className="border border-black px-1 py-1">{r?.fld_tyreserailno ?? ""}</td>
                        <td className="border border-black px-1 py-1 text-center">{r?.fld_tyresize ?? ""}</td>
                        <td className="border border-black px-1 py-1">{r?.fld_tyremake ?? ""}</td>
                        <td className="border border-black px-1 py-1">{r?.fld_tyremodel ?? ""}</td>
                        <td className="border border-black px-1 py-1">{r?.fld_vehicleregno ?? ""}</td>
                        <td className="border border-black px-1 py-1 text-center">{r?.fld_vehicle_code ?? ""}</td>
                        <td className="border border-black px-1 py-1 text-center">{r?.fld_status ?? ""}</td>
                        <td className="border border-black px-1 py-1 text-center">{fmtDate(r?.fld_fittingdate)}</td>
                        <td className="border border-black px-1 py-1 text-center">{fmtDate(r?.fld_removingdate)}</td>
                        <td className="border border-black px-1 py-1 text-right">{fmtNum(r?.fld_fittingkmhr)}</td>
                        <td className="border border-black px-1 py-1 text-right">{fmtNum(r?.fld_RemovingKmhr)}</td>
                        <td className="border border-black px-1 py-1 text-right">{fmtNum(r?.fld_runninghrkm)}</td>
                        <td className="border border-black px-1 py-1 text-right">{fmtNum(r?.fld_tyrerate)}</td>
                        <td className="border border-black px-1 py-1 text-right">{fmtNum(r?.fld_tyrecost)}</td>
                        <td className="border border-black px-1 py-1">{r?.fld_reason ?? ""}</td>
                      </tr>
                    ))}

                    <tr className="bg-yellow-100 font-bold">
                      <td colSpan={10} className="border border-black px-1 py-1 text-right">
                        Totals
                      </td>
                      <td className="border border-black px-1 py-1 text-right">{fmtNum(totals.fittingHr)}</td>
                      <td className="border border-black px-1 py-1 text-right">{fmtNum(totals.removingHr)}</td>
                      <td className="border border-black px-1 py-1 text-right">{fmtNum(totals.runningHr)}</td>
                      <td className="border border-black px-1 py-1 text-right">{fmtNum(totals.costOfTyre)}</td>
                      <td className="border border-black px-1 py-1 text-right">{fmtNum(totals.costHr)}</td>
                      <td className="border border-black px-1 py-1"></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}

        {!loading && printedRecords === 0 ? (
          <div className="border-2 border-black p-4 text-center">No Records</div>
        ) : null}

        <div className="mt-2 text-xs border-2 border-black p-1">
          <p>Report generated by ERP-Software - www.infracnsoft.com owned by www.nissiinfotech.com</p>
        </div>
      </div>
    </div>
  );
}
